/**
 * Bounty display and input helpers.
 *
 * Pure functions for bounty UI: numeric price input filtering, formatting a
 * bounty's category/sex/type into a "fashion...male...shoes" style label,
 * building "submit a/an <item>" call-to-action text, and formatting the
 * posted date. Consumed by ItemCollection and the bounty display/submit
 * pages (BountyDisplay, SubheadedBountyDisplay, BountyViewSubmit).
 */
import { Bounty } from "@/types/bounty";
import { Category, Sex } from "@/types/category";

export function filterAndValidateNumericInput(input: string): number | null {
    if (input === "") {
        return null;
    }
    // Remove all non-numeric characters
    const numericString = input.replace(/\D/g, "");

    // If after removing non-numeric characters we have an empty string, return null
    if (numericString === "") {
        return null;
    }

    // Convert the numeric string (which will strip leading 0's) to a number and return
    return Number(numericString);
}

export function formatCategorySexCategoryType(
    category: Category | null,
    sex: Sex | null,
    categoryType: string | null
): string {
    if (!category) {
        return "any category";
    }
    let formattedString = `${category}`;
    if (sex) {
        if (formattedString.length > 0) {
            formattedString += "...";
        }
        formattedString += `${sex}`;
    }
    if (categoryType) {
        if (formattedString.length > 0) {
            formattedString += "...";
        }
        formattedString += `${categoryType}`;
    }
    return formattedString;
}

export function formatBountyCategorySexCategoryType(bounty: Bounty): string {
    const { category, sex, categoryType } = bounty;
    return formatCategorySexCategoryType(category, sex, categoryType);
}

export function submitBountyText(bounty: Bounty) {
    const { category, categoryType } = bounty;
    if (categoryType) {
        // Check if the last letter of categoryType is 's'
        if (categoryType.charAt(categoryType.length - 1).toLowerCase() === "s") {
            return `submit a pair of ${categoryType}`;
        }
        const firstChar = categoryType.charAt(0).toLowerCase();
        if ("aeiou".includes(firstChar)) {
            return `submit an ${categoryType}`;
        } else {
            return `submit a ${categoryType}`;
        }
    } else if (category == "watches") {
        return "submit a watch";
    }
}

export function formatPostedDatetime(datetime: string) {
    const date = new Date(datetime);

    // Format with full month name, day, and year
    const formattedDate = date.toLocaleDateString("en-US", {
        month: "long", // "long" gives the full month name
        day: "numeric",
        year: "numeric",
    });

    return `posted on ${formattedDate}`;
}
